import React from "react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { useToast } from "../hooks/use-toast";
import { Mail } from "lucide-react";
import { supabase } from "../lib/supabase";
import { useNavigate } from "react-router-dom";

const Contact = () => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [isSubmitted, setIsSubmitted] = React.useState(false);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);

    const form = e.currentTarget;
    const formData = {
      name: (form.elements.namedItem('name') as HTMLInputElement).value,
      email: (form.elements.namedItem('email') as HTMLInputElement).value,
      subject: (form.elements.namedItem('subject') as HTMLInputElement).value,
      message: (form.elements.namedItem('message') as HTMLTextAreaElement).value,
    };

    try {
      const { data, error } = await supabase.functions.invoke('send-contact-form', {
        body: formData,
      });

      if (error) {
        let errorMessage = "Please try again later or contact us directly at support@meetmemoize.com";
        
        // Handle specific error cases
        if (error.message.includes("Too many requests")) {
          errorMessage = "You've sent too many messages. Please try again later.";
        } else if (error.message.includes("Invalid email")) {
          errorMessage = "Please enter a valid email address.";
        } else if (error.message.includes("All fields are required")) {
          errorMessage = "Please fill in all required fields.";
        }

        toast({
          title: "Error sending message",
          description: errorMessage,
          variant: "destructive",
        });
        return;
      }

      toast({
        title: "Message sent",
        description: "We'll get back to you as soon as possible.",
      });

      // Set submitted state to true
      setIsSubmitted(true);

    } catch (error) {
      console.error('Error sending contact form:', error);
      toast({
        title: "Error sending message",
        description: "Please try again later or contact us directly at support@meetmemoize.com",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSubmitted) {
    return (
      <div className="container mx-auto px-4 py-16">
        <div className="max-w-2xl mx-auto text-center">
          <div className="mb-8">
            <Mail className="h-12 w-12 text-primary mx-auto mb-4" />
            <h1 className="text-3xl font-bold mb-4">Thank You!</h1>
            <p className="text-muted-foreground mb-8">
              We've received your message and will get back to you as soon as possible.
            </p>
          </div>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button onClick={() => navigate("/")} variant="default">
              Return Home
            </Button>
            <Button onClick={() => navigate("/how-it-works")} variant="outline">
              Learn More
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-16">
      <div className="max-w-2xl mx-auto">
        <div className="flex items-center gap-2 mb-8">
          <Mail className="h-8 w-8 text-primary" />
          <h1 className="text-4xl font-bold">Contact Us</h1>
        </div>
        
        <p className="text-muted-foreground mb-8">
          Have questions or need assistance? We're here to help! Fill out the form below and our team will get back to you shortly.
        </p>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label htmlFor="name" className="block text-sm font-medium mb-2">
              Name
            </label>
            <Input id="name" required placeholder="Your name" />
          </div>

          <div>
            <label htmlFor="email" className="block text-sm font-medium mb-2">
              Email
            </label>
            <Input 
              id="email" 
              type="email" 
              required 
              placeholder="your@email.com"
              pattern="[^@\s]+@[^@\s]+\.[^@\s]+" 
              title="Please enter a valid email address"
            />
          </div>

          <div>
            <label htmlFor="subject" className="block text-sm font-medium mb-2">
              Subject
            </label>
            <Input id="subject" required placeholder="How can we help?" />
          </div>

          <div>
            <label htmlFor="message" className="block text-sm font-medium mb-2">
              Message
            </label>
            <Textarea
              id="message"
              required
              placeholder="Tell us more about your inquiry..."
              className="min-h-[150px]"
            />
          </div>

          <Button type="submit" size="lg" className="w-full" disabled={isSubmitting}>
            {isSubmitting ? "Sending..." : "Send Message"}
          </Button>
        </form>
      </div>
    </div>
  );
};

export default Contact;